import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import sortBy from '@nullvoxpopuli/ember-composable-helpers/helpers/sort-by';
import { task, type Task } from 'ember-concurrency';
import { not } from 'ember-truth-helpers';
import type Flow from 'garaje/models/flow';
import type SignInField from 'garaje/models/sign-in-field';
import { SIGN_IN_FIELD_KINDS } from 'garaje/utils/enums';
import { filter } from 'macro-decorators';
import { localCopy } from 'tracked-toolbox';

interface SignInFieldsDataStorageSignature {
  Args: {
    signInFields: SignInField[];
    flow: Flow;
    saveFieldsTask: Task<void, []>;
    isDisabled?: boolean;
    isPristine: boolean;
    onChange?: (field: SignInField, attr: string) => void;
  };
}

const configurableFieldsFilterFn = (field: SignInField): boolean => {
  return field.identifier !== 'host' && field.kind === SIGN_IN_FIELD_KINDS.SINGLE_SELECTION;
};

export default class SignInFieldsDataStorage extends Component<SignInFieldsDataStorageSignature> {
  @localCopy('args.signInFields') declare signInFields: SignInField[];

  optionsName = 'data-storage';

  @filter('signInFields', configurableFieldsFilterFn)
  declare configurableDropdownFields: SignInField[];

  get isCTAVisible(): boolean {
    if (this.args.isDisabled) return false;

    return this.isNotEmpty;
  }

  get isCTADisabled(): boolean {
    return this.args.isDisabled || this.args.isPristine || this.saveDataStorageOptionsTask.isRunning;
  }

  get isNotEmpty(): boolean {
    return this.configurableDropdownFields.length > 0;
  }

  @action
  onChange(field: SignInField, attr: string): void {
    this.args.onChange?.(field, attr);
  }

  @action
  storeField(field: SignInField): void {
    if (!field.storeResponse) {
      this.onChange(field, 'storeResponse');
    }

    field.storeResponse = true;
  }

  @action
  discardField(field: SignInField): void {
    if (field.storeResponse) {
      this.onChange(field, 'storeResponse');
    }

    field.storeResponse = false;
  }

  saveDataStorageOptionsTask = task(async (): Promise<void> => {
    const { flow, saveFieldsTask } = this.args;

    flow.storeResponseConfig = 'managed';

    await flow.save();
    await saveFieldsTask.perform();
  });

  <template>
    <div class="px-8 overflow-none" data-test-field-options={{this.optionsName}}>
      <p class="text-sm text-carbon-60 mb-6 pr-12">
        Choose whether to automatically store or discard visitor dropdown responses. Learn more about managing
        <a
          href="https://envoy.help/en/articles/4519782-manage-how-you-retain-employee-visitor-responses"
          target="_blank"
          rel="noopener"
        >
          data storage →
        </a>
      </p>

      {{#if this.isNotEmpty}}
        <div class="mb-8">
          <div data-test-field-list class="mt-6">
            <div class="flex text-carbon-50 font-bold">
              <span class="flex text-sm" style="flex:3">Field</span>
              <span id="dataStorageHeadingSaveResponse" class="flex-1 flex text-sm justify-center">Save response</span>
              <span id="dataStorageHeadingDiscardResponse" class="flex-1 flex text-sm justify-center">Discard response</span>
            </div>

            <hr class="my-2" />

            <div class="overflow-y-auto overscroll-y-contain" style="max-height:40vh;">
              {{#each (sortBy "position" this.configurableDropdownFields) as |field|}}
                <div
                  data-test-configurable-field={{field.id}}
                  class="flex"
                  role="radiogroup"
                  aria-labelledby="dataStorageFieldName-{{field.id}}"
                >
                  <p id="dataStorageFieldName-{{field.id}}" class="flex text-sm text-carbon-50 m-0" style="flex:3">
                    {{field.name}}
                  </p>

                  <div class="flex-1 flex items-center">
                    <input
                      data-test-store-field
                      class="my-0 mx-auto"
                      type="radio"
                      name="data-storage-{{field.id}}"
                      disabled={{@isDisabled}}
                      checked={{field.storeResponse}}
                      aria-labelledby="dataStorageHeadingSaveResponse"
                      {{on "click" (fn this.storeField field)}}
                    />
                  </div>

                  <div class="flex-1 flex items-center">
                    <input
                      data-test-discard-field
                      class="my-0 mx-auto"
                      type="radio"
                      name="data-storage-{{field.id}}"
                      disabled={{@isDisabled}}
                      checked={{not field.storeResponse}}
                      aria-labelledby="dataStorageHeadingDiscardResponse"
                      {{on "click" (fn this.discardField field)}}
                    />
                  </div>
                </div>

                <hr class="my-2" />
              {{/each}}
            </div>
          </div>
        </div>
      {{else}}
        <p data-test-empty-text class="text-center text-carbon-40 mt-12 mb-24">
          You have no dropdown fields to
          {{if @isDisabled "view" "manage responses for"}}.
        </p>
      {{/if}}

      {{#if this.isCTAVisible}}
        <button
          disabled={{this.isCTADisabled}}
          class="btn btn--small btn--brand float-right mb-8
            {{if this.saveDataStorageOptionsTask.isRunning 'disabled'}}
            "
          {{on "click" this.saveDataStorageOptionsTask.perform}}
          data-test-save-options={{this.optionsName}}
        >
          {{#if this.saveDataStorageOptionsTask.isRunning}}
            Saving...
          {{else}}
            Save
          {{/if}}
        </button>
      {{/if}}
    </div>
  </template>
}
